import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import PageHeader from "../components/layout/PageHeader";
import { Table } from "../components/ui/Table";
import { Input } from "../components/ui/Input";
import { Avatar } from "../components/ui/Avatar";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { MultiSelect } from "../components/ui/MultiSelect";
import CamperForm from "../components/forms/CamperForm";
import { useCampers } from "../hooks/useCampers";
import { useDebounce } from "../hooks/useDebounce";
import { useAuth } from "../hooks/useAuth";
import { SESSION_DISPLAY_LABEL, useSeason } from "../context/SeasonContext";
import { cabinGroupsApi } from "../api/cabinGroups";
import { formatCabinNumber, normalizeDivisionName } from "../utils/terminology";
import type { AttendanceFilter } from "../api/campers";
import type { Camper, Division, Enrollment } from "../types";

const RANGERS_NAME = "Rangers";

/** Resolve display-ready division name for a camper's enrollment. */
function divisionName(enrollment: Enrollment | undefined): string {
  if (!enrollment) return "—";
  const raw = enrollment.division?.name ?? enrollment.cabin?.cabinGroup?.division?.name;
  return raw ? normalizeDivisionName(raw) : "—";
}

export default function Campers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrDirector = user?.role === "admin" || user?.role === "director";
  const { selectedYear, selectedTermId, activeSession } = useSeason();

  const [search, setSearch] = useState("");
  // Selected division *names* (post-normalization), so duplicate CampMinder
  // rows that share a name (e.g. boys/girls "Cubs") collapse to one option.
  const [selectedDivisionNames, setSelectedDivisionNames] = useState<string[]>([]);
  const [divisionDefaultsApplied, setDivisionDefaultsApplied] = useState(false);
  const [team, setTeam] = useState("");
  const [attendance, setAttendance] = useState<AttendanceFilter>("all");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const debouncedSearch = useDebounce(search, 300);

  const [divisions, setDivisions] = useState<Division[]>([]);
  useEffect(() => {
    cabinGroupsApi.listDivisions().then(setDivisions).catch(() => {});
  }, []);

  // Unique division names (sorted by sortOrder, then name) — drives the
  // multi-select options. Multiple Division rows can share a name; each
  // option carries every matching ID for filtering.
  const divisionOptions = useMemo(() => {
    const byName = new Map<string, { name: string; ids: number[]; sortOrder: number }>();
    for (const d of divisions) {
      if (d.archivedAt) continue;
      const name = normalizeDivisionName(d.name);
      const existing = byName.get(name);
      if (existing) {
        existing.ids.push(d.id);
        existing.sortOrder = Math.min(existing.sortOrder, d.sortOrder ?? 0);
      } else {
        byName.set(name, { name, ids: [d.id], sortOrder: d.sortOrder ?? 0 });
      }
    }
    return Array.from(byName.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
  }, [divisions]);

  // Default selection: every division except Rangers. Apply once after the
  // divisions list first loads so users can intentionally clear the filter.
  useEffect(() => {
    if (divisionDefaultsApplied) return;
    if (divisionOptions.length === 0) return;
    setSelectedDivisionNames(
      divisionOptions.map((o) => o.name).filter((n) => n !== RANGERS_NAME),
    );
    setDivisionDefaultsApplied(true);
  }, [divisionOptions, divisionDefaultsApplied]);

  // Convert selected names → IDs for the backend filter. When *every* option
  // is selected we send no filter (saves a redundant Op.in over all IDs).
  // When *zero* options are selected we send a sentinel ID so the backend
  // returns no campers — matches what the user sees in the dropdown.
  const divisionIds = useMemo<number[] | undefined>(() => {
    if (!divisionDefaultsApplied) return undefined;
    if (divisionOptions.length === 0) return undefined;
    if (selectedDivisionNames.length === divisionOptions.length) return undefined;
    if (selectedDivisionNames.length === 0) return [-1];
    const selected = new Set(selectedDivisionNames);
    return divisionOptions.flatMap((o) => (selected.has(o.name) ? o.ids : []));
  }, [selectedDivisionNames, divisionOptions, divisionDefaultsApplied]);

  const divisionFilterActive =
    divisionDefaultsApplied &&
    divisionOptions.length > 0 &&
    selectedDivisionNames.length !== divisionOptions.length;

  // Reset to page 1 whenever any filter changes
  useEffect(() => { setPage(1); }, [debouncedSearch, divisionIds, team, attendance, selectedYear, selectedTermId]);

  const { data, loading } = useCampers({
    search: debouncedSearch,
    page,
    pageSize: 25,
    year: selectedYear,
    termId: selectedTermId ?? undefined,
    divisionIds,
    team: team || undefined,
    attendance: attendance === "all" ? undefined : attendance,
    refreshKey,
  });

  const hasFilter = !!debouncedSearch || divisionFilterActive || !!team || attendance !== "all";

  const clearFilters = () => {
    setSearch("");
    setSelectedDivisionNames(divisionOptions.map((o) => o.name));
    setTeam("");
    setAttendance("all");
  };

  const sessionLabel = SESSION_DISPLAY_LABEL[activeSession];

  const columns = [
    {
      key: "name",
      header: "Camper",
      render: (row: Camper) => (
        <div className="flex items-center gap-3">
          <Avatar firstName={row.firstName} lastName={row.lastName} size="sm" />
          <span className="font-medium text-stone-900">
            {row.firstName} {row.lastName}
          </span>
          {row.isFullTerm && (
            <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-forest-100 text-forest-800">
              Full Term
            </span>
          )}
        </div>
      ),
    },
    {
      key: "cabin",
      header: "Cabin",
      render: (row: Camper) => {
        const enrollment = row.enrollments?.[0];
        const cabinName = enrollment?.overrideCabin?.name ?? enrollment?.cabin?.name;
        return (
          <span className="text-stone-600">
            {cabinName ? formatCabinNumber(cabinName) : "—"}
          </span>
        );
      },
    },
    {
      key: "division",
      header: "Division",
      render: (row: Camper) => {
        const name = divisionName(row.enrollments?.[0]);
        return name !== "—"
          ? <Badge label={name} color="green" />
          : <span className="text-stone-400">—</span>;
      },
    },
    {
      key: "team",
      header: "Team",
      render: (row: Camper) => {
        if (row.team === "army") return <Badge label="Army" color="army" />;
        if (row.team === "navy") return <Badge label="Navy" color="navy" />;
        return <span className="text-stone-400">—</span>;
      },
    },
    {
      key: "completions",
      header: "ACs",
      render: (row: Camper) => (
        <span className="tabular-nums font-medium text-stone-700">
          {row.completionCount ?? 0}
        </span>
      ),
    },
    {
      key: "honors",
      header: "Honors",
      render: (row: Camper) => (
        <span className="tabular-nums font-medium text-amber-600">
          {row.honorCount ?? 0}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Campers"
      />

      <div className="p-6 lg:p-8 space-y-5">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Name search */}
          <div className="flex-1 min-w-48 sm:max-w-xs">
            <Input
              placeholder="Search campers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            />
          </div>

          {/* Division filter (multi-select; defaults to every division except Rangers) */}
          <MultiSelect
            ariaLabel="Filter by division"
            className="w-44"
            options={divisionOptions.map((o) => ({ value: o.name, label: o.name }))}
            selected={selectedDivisionNames}
            onChange={setSelectedDivisionNames}
            allLabel="No divisions"
            everyLabel="All divisions"
            formatSelected={(opts) =>
              opts.length <= 2
                ? opts.map((o) => o.label).join(", ")
                : `${opts.length} divisions`
            }
          />

          {/* Team filter */}
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="py-2 pl-3 pr-8 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-stone-700"
          >
            <option value="">All teams</option>
            <option value="army">Army</option>
            <option value="navy">Navy</option>
            <option value="unassigned">Unassigned</option>
          </select>

          {/* Session attendance filter */}
          <select
            value={attendance}
            onChange={(e) => setAttendance(e.target.value as AttendanceFilter)}
            aria-label="Session attendance"
            disabled={activeSession === "rangers"}
            title={activeSession === "rangers" ? "Full Term doesn't apply to Rangers" : undefined}
            className="py-2 pl-3 pr-8 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="all">All campers in {sessionLabel}</option>
            <option value="session-only">{sessionLabel} only</option>
            <option value="full-term">Full Term campers</option>
          </select>

          {/* Clear filters */}
          {hasFilter && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear
            </button>
          )}

          {/* Result count */}
          {data && (
            <span className="text-xs text-stone-400 ml-auto">
              {data.total} camper{data.total !== 1 ? "s" : ""}
            </span>
          )}

          {/* Add camper — secondary action; CampMinder sync is the primary path. */}
          {isAdminOrDirector && (
            <button
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 text-stone-700 transition-colors"
              title="For campers not synced from CampMinder"
            >
              <PlusIcon className="w-4 h-4" />
              Add camper
            </button>
          )}
        </div>
        {isAdminOrDirector && (
          <p className="text-xs text-stone-400 -mt-2">
            Manual add is for campers not synced from CampMinder.
          </p>
        )}

        {/* Table */}
        <Table
          columns={columns}
          data={data?.data ?? []}
          keyExtractor={(row) => row.id}
          onRowClick={(row) => navigate(`/campers/${row.id}`)}
          loading={loading}
          emptyMessage="No campers found. Try adjusting your search or filters."
        />

        {/* Pagination */}
        {data && data.total > data.pageSize && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              Showing {(page - 1) * data.pageSize + 1}–
              {Math.min(page * data.pageSize, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Camper" size="md">
        <CamperForm
          onCancel={() => setAddOpen(false)}
          onSuccess={(c) => {
            setAddOpen(false);
            setRefreshKey((k) => k + 1);
            navigate(`/campers/${c.id}`);
          }}
        />
      </Modal>
    </div>
  );
}
